import { useEffect, useId, useMemo, useState } from "react";
import { FiClipboard, FiSave, FiX } from "react-icons/fi";
import { Input, Modal } from "@iq/models";
import {
	addProjectMember,
	createProject,
	getTeamMembers,
} from "../../Services/pm_service";
import "../../../CRM/components/modals/Modal.css";

function CreateProjectModal({ isOpen, onClose, onCreated }) {
	const formId = useId();
	const [formData, setFormData] = useState({
		title: "",
		description: "",
	});
	const [selectedMemberLabel, setSelectedMemberLabel] = useState("");
	const [teamMembers, setTeamMembers] = useState([]);
	const [membersLoading, setMembersLoading] = useState(false);
	const [selectedMembers, setSelectedMembers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const availableMembers = useMemo(
		() =>
			teamMembers.filter(
				(member) => !selectedMembers.some((selected) => selected.id === member.id)
			),
		[teamMembers, selectedMembers]
	);

	const canSubmit = useMemo(() => {
		return formData.title.trim().length > 0 && !loading;
	}, [formData.title, loading]);

	const actionButtons = useMemo(
		() => [
			{
				key: "create-project",
				text: loading ? "Creating..." : "Create Project",
				icon: <FiSave />,
				type: "submit",
				form: formId,
				disabled: !canSubmit,
				color: "accent",
			},
		],
		[canSubmit, formId, loading]
	);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		let cancelled = false;

		async function loadTeamMembers() {
			setMembersLoading(true);
			try {
				const members = await getTeamMembers();
				if (!cancelled) {
					setTeamMembers(Array.isArray(members) ? members : []);
				}
			} catch (membersError) {
				if (!cancelled) {
					setTeamMembers([]);
					setError(membersError?.message || "Failed to load team members");
				}
			} finally {
				if (!cancelled) {
					setMembersLoading(false);
				}
			}
		}

		loadTeamMembers();

		return () => {
			cancelled = true;
		};
	}, [isOpen]);

	if (!isOpen) {
		return null;
	}

	function handleInputChange(event) {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		setError("");
	}

	function handleClose() {
		setFormData({ title: "", description: "" });
		setSelectedMembers([]);
		setSelectedMemberLabel("");
		setTeamMembers([]);
		setMembersLoading(false);
		setError("");
		onClose?.();
	}

	function handleAddMember(member) {
		if (selectedMembers.some((existing) => existing.id === member.id)) {
			setError(`${member.name} is already selected`);
			return;
		}

		setSelectedMembers((prev) => [
			...prev,
			{
				id: member.id,
				name: member.name,
				email: member.email,
			},
		]);
		setSelectedMemberLabel("");
		setError("");
	}

	function handleRemoveMember(memberId) {
		setSelectedMembers((prev) => prev.filter((member) => member.id !== memberId));
	}

	async function handleSubmit(event) {
		event.preventDefault();
		setError("");

		const title = formData.title.trim();
		if (!title) {
			setError("Project title is required");
			return;
		}

		try {
			setLoading(true);
			const createdProject = await createProject({
				title,
				description: formData.description.trim(),
			});

			if (selectedMembers.length > 0) {
				const results = await Promise.allSettled(
					selectedMembers.map((member) =>
						addProjectMember(createdProject.id, { userId: member.id, role: "member" })
					)
				);

				const failed = results.filter((result) => result.status === "rejected");
				if (failed.length > 0) {
					console.error("Some project members failed to add", failed);
					window.alert(
						`Project created, but ${failed.length} member(s) could not be added.`
					);
				}
			}

			onCreated?.(createdProject);
			handleClose();
		} catch (submitError) {
			setError(submitError?.message || "Failed to create project");
		} finally {
			setLoading(false);
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Create Project"
			icon={<FiClipboard />}
			actionButtons={actionButtons}
			errorMessage={error}
			shrink
		>
			<form id={formId} onSubmit={handleSubmit} className="modal-form-container">
				<div className="form-fields-container">
					<div className="modal-form-field wide-form-field">
						<label htmlFor="pm-project-title">
							Project Name <span style={{ color: "var(--color-danger)" }}>*</span>
						</label>
						<Input
							id="pm-project-title"
							type="text"
							name="title"
							value={formData.title}
							onChange={handleInputChange}
							placeholder="E.g. Website Revamp"
							required
							disabled={loading}
							colorScheme="light"
						/>
					</div>

					<div className="modal-form-field wide-form-field">
						<label className="attendees-label">
							Project Members
							{selectedMembers.length > 0 ? (
								<span className="deal-product-pills">
									{selectedMembers.map((member) => (
										<span key={member.id} className="deal-product-pill">
											<span className="deal-product-pill-text">{member.name}</span>
											<button
												type="button"
												className="deal-product-pill-remove"
												onClick={() => handleRemoveMember(member.id)}
												disabled={loading}
												aria-label={`Remove ${member.name}`}
											>
												<FiX />
											</button>
										</span>
									))}
								</span>
							) : (
								<span className="deal-product-pill--empty">No members selected</span>
							)}
						</label>
						<div className="attendees-control-row">
							{membersLoading ? (
								<div className="attendee-loading">Loading team members...</div>
							) : (
								<Input
									type="dropdown"
									id="pm-project-members"
									name="pm-project-members"
									className="attendees-dropdown"
									placeholder={
										availableMembers.length === 0
											? "All team members selected"
											: "Select members to add..."
									}
									value={selectedMemberLabel}
									allowCustom={false}
									colorScheme="light"
									ariaLabel="Project members"
									renderInPortal
									disabled={loading || availableMembers.length === 0}
								>
									{({ closeMenu }) => (
										<>
											{availableMembers.map((member) => (
												<button
													key={member.id}
													type="button"
													className="dropdown-menu-item"
													onClick={() => {
														handleAddMember(member);
														setSelectedMemberLabel("");
														closeMenu();
													}}
												>
													{member.name} ({member.email})
												</button>
											))}
											{availableMembers.length === 0 ? (
												<div className="dropdown-menu-empty">All team members selected</div>
											) : null}
										</>
									)}
								</Input>
							)}
						</div>
					</div>

					<div className="modal-form-field wide-form-field">
						<label htmlFor="pm-project-description">Description</label>
						<Input
							id="pm-project-description"
							type="textarea"
							name="description"
							value={formData.description}
							onChange={handleInputChange}
							placeholder="Optional project description"
							rows={3}
							disabled={loading}
							colorScheme="light"
						/>
					</div>
				</div>
			</form>
		</Modal>
	);
}

export default CreateProjectModal;
